import React, { useState, useCallback } from 'react';
import { useCartViewModel } from '../../viewmodels/useCartViewModel';
import { useTheme } from '../../themes/ThemeProvider';
import { cn } from '../../utils/cn';
import { LogoConfig } from '../../themes/themeTypes';
import CategoryMenu from '../navigation/CategoryMenu';
import MobileNav from '../navigation/MobileNav';
import ThemeToggle from '../ui/ThemeToggle';
import LocaleSwitcher from './LocaleSwitcher';
import { useScrollPast } from '../../hooks/useScrollPast';

const NAV_SPACING_CLASS: Record<'compact' | 'comfortable' | 'relaxed', string> = {
  compact: 'gap-4',
  comfortable: 'gap-6',
  relaxed: 'gap-8',
};

const NAV_FONT_WEIGHT_CLASS: Record<'light' | 'normal' | 'medium' | 'semibold' | 'bold', string> = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

/**
 * Render logo based on branding configuration. When `scrolled` is true and
 * the logo declares an `imageUrlScrolled` (compact icon variant), the two
 * images crossfade and the container shrinks. Pattern mirrors Caroline
 * Svedbom's sticky-header shrink behavior.
 */
const Logo: React.FC<{
  logo: LogoConfig | null | undefined;
  brandName: string;
  scrolled?: boolean;
}> = ({ logo, brandName, scrolled = false }) => {
  if (!logo || logo.type === 'hidden') {
    return null;
  }

  const baseClasses = 'inline-flex items-center';

  if (logo.type === 'svg' && logo.svgPath) {
    return (
      <svg
        viewBox={logo.svgViewBox || '0 0 100 100'}
        width={logo.svgWidth || '48'}
        height={logo.svgHeight || '48'}
        className={cn(baseClasses, logo.className)}
        aria-label={brandName}
        role="img"
        focusable="false"
      >
        <path d={logo.svgPath} fill="currentColor" />
      </svg>
    );
  }

  if (logo.type === 'image' && logo.imageUrl) {
    const scrolledUrl = logo.imageUrlScrolled;
    const scrolledWidth = logo.imageWidthScrolled || '40';
    const scrolledHeight = logo.imageHeightScrolled || '40';
    const logoFilter =
      'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))';
    const fullW = Number(logo.imageWidth) || 64;
    const fullH = Number(logo.imageHeight) || 64;
    const compactW = Number(scrolledWidth) || 40;
    const compactH = Number(scrolledHeight) || 40;

    // When a scrolled variant is declared, render both images and crossfade.
    // When not, just render the single image unchanged.
    if (scrolledUrl) {
      return (
        <span
          className={cn(baseClasses, 'relative block')}
          style={{
            width: scrolled ? `${compactW}px` : `${fullW}px`,
            height: scrolled ? `${compactH}px` : `${fullH}px`,
            transition: 'width 600ms cubic-bezier(0.215, 0.61, 0.355, 1), height 600ms cubic-bezier(0.215, 0.61, 0.355, 1)',
          }}
        >
          <img
            src={logo.imageUrl}
            alt={logo.imageAlt || brandName}
            className={cn('absolute inset-0 w-full h-full object-contain', logo.className)}
            style={{
              filter: logoFilter,
              opacity: scrolled ? 0 : 1,
              transition: 'opacity 600ms cubic-bezier(0.215, 0.61, 0.355, 1)',
            }}
          />
          <img
            src={scrolledUrl}
            alt=""
            aria-hidden="true"
            className={cn('absolute inset-0 w-full h-full object-contain', logo.className)}
            style={{
              filter: logoFilter,
              opacity: scrolled ? 1 : 0,
              transition: 'opacity 600ms cubic-bezier(0.215, 0.61, 0.355, 1)',
            }}
          />
        </span>
      );
    }

    return (
      <img
        src={logo.imageUrl}
        alt={logo.imageAlt || brandName}
        width={logo.imageWidth}
        height={logo.imageHeight}
        className={cn(baseClasses, logo.className)}
        style={{ filter: logoFilter }}
      />
    );
  }

  // Default to text logo
  return (
    <span className={cn('text-2xl font-semibold tracking-tight font-serif', logo.className)}>
      {logo.text || brandName}
    </span>
  );
};

const Header: React.FC = () => {
  const { totals, toggleCartDrawer } = useCartViewModel();
  const { branding } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Fallback for when branding is still loading (shouldn't happen due to ThemeProvider)
  const brandName = branding?.store?.name || branding?.name || 'Store';
  const brandTagline = branding?.store?.tagline || branding?.tagline;
  const showBrandName = branding?.header?.showBrandName ?? true;
  const headerLogo = branding?.header?.logo;
  const fallbackLogo = branding?.logo;
  const logoConfig = headerLogo?.type === 'hidden'
    ? null
    : headerLogo && headerLogo.type
      ? headerLogo
      : fallbackLogo?.type === 'hidden'
        ? null
        : fallbackLogo ?? null;

  const hasLogo = Boolean(logoConfig);
  const navigationItems = branding?.header?.navigation?.items || [
    { label: 'Home', path: '/' },
    { label: 'Products', path: '/products' },
    { label: 'About', path: '/about' }
  ];

  const navigationStyling = branding?.header?.navigation?.styling;
  const navSpacingKey = navigationStyling?.spacing || 'comfortable';
  const navSpacingClass = NAV_SPACING_CLASS[navSpacingKey] || NAV_SPACING_CLASS.comfortable;
  const navFontWeightClass = navigationStyling?.fontWeight ? NAV_FONT_WEIGHT_CLASS[navigationStyling.fontWeight] : 'font-normal';
  const navLetterSpacing = navigationStyling?.letterSpacing;
  const navTextTransformClass = navigationStyling?.textTransform === 'uppercase'
    ? `uppercase ${navLetterSpacing ? `tracking-[${navLetterSpacing}]` : 'tracking-[0.1em]'}`
    : navigationStyling?.textTransform === 'capitalize'
      ? 'capitalize'
      : navigationStyling?.textTransform === 'lowercase'
        ? 'lowercase'
        : '';

  const categoryMenuConfig = branding?.header?.navigation?.categoryMenu;
  const categoryPosition = categoryMenuConfig?.position || 'in-nav';

  const headerLayout = branding?.header?.layout || 'standard';
  const logoPosition = branding?.header?.logoPosition || 'left';
  const centerNavigation = headerLayout === 'logo-center-name-left' || logoPosition === 'center';
  const headerStyling = branding?.header?.styling;
  // Default to theme's bg token so header follows the active theme mode.
  // Stores can override with a hex literal or another CSS var.
  const headerBg = headerStyling?.backgroundColor || 'var(--color-bg)';
  const headerBorderClass = headerStyling?.borderBottom === false ? '' : 'border-b';
  const headerSticky = headerStyling?.sticky === false ? '' : 'sticky top-0 z-50';

  // Scroll-compact behavior — opt-in per store via branding.header.styling.scrollCompact.
  // When true, the wordmark fades out + the brand mark shrinks as the user scrolls
  // down. At scroll-top the header returns to its full size. Pattern borrowed from
  // carolinesvedbom.com (Shopify Broadcast theme).
  const scrollCompactEnabled = headerStyling?.scrollCompact === true;
  const isScrolled = useScrollPast(40) && scrollCompactEnabled;

  return (
    <header
      data-stuck={isScrolled || undefined}
      className={`${headerBorderClass} ${headerSticky} shadow-sm transition-all duration-300`}
      style={{
        backgroundColor: headerBg,
        borderColor: 'var(--color-border-light)',
      }}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary focus:text-[var(--color-text-on-primary)] focus:rounded-sm focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <div
        className="container mx-auto px-lg py-md flex items-center gap-6"
        style={{ maxWidth: 'var(--container-max-width)' }}
      >
        {/* Mobile Hamburger Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden text-text-secondary hover:text-primary transition-colors p-2 -m-2 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? (
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          )}
        </button>

        {/* Logo and Brand Name */}
        <div
          className={cn(
            'flex items-center gap-3 justify-start flex-shrink-0',
            'md:flex-1',
            logoPosition === 'center' ? 'md:justify-center md:w-full' : ''
          )}
        >
          <a
            href="/"
            className={cn(
              'no-underline text-text-primary hover:text-primary transition-colors',
              hasLogo ? 'flex items-center gap-3' : 'flex flex-col items-start gap-1'
            )}
            aria-label={`${brandName} Home`}
          >
            {logoConfig && <Logo logo={logoConfig} brandName={brandName} scrolled={isScrolled} />}
            {showBrandName && !(logoConfig?.type === 'text') && (
              <span
                className="text-2xl font-semibold tracking-tight text-text-primary"
                style={{
                  fontFamily: 'var(--font-family-brand, var(--font-family-heading))',
                  // When scrollCompact is active and we're past the threshold,
                  // fade out the wordmark and collapse its space so only the
                  // small logo mark remains (carolinesvedbom.com pattern).
                  opacity: isScrolled ? 0 : 1,
                  maxWidth: isScrolled ? '0' : '100%',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition:
                    'opacity 400ms cubic-bezier(0.215, 0.61, 0.355, 1), max-width 500ms cubic-bezier(0.215, 0.61, 0.355, 1)',
                }}
              >
                {brandName}
              </span>
            )}
            {!logoConfig && brandTagline && (
              <span className="hidden lg:block text-xs tracking-[0.3em] uppercase text-primary/70 mt-1">
                {brandTagline}
              </span>
            )}
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav
          className={cn(
            'hidden md:flex items-center justify-center md:flex-1',
            navSpacingClass,
          )}
          data-testid="desktop-nav"
        >
          {navigationItems.map((item, index) => (
            <a
              key={index}
              href={item.path}
              className={cn(
                'no-underline text-text-primary text-sm transition-all hover:text-primary border-b-2 border-transparent hover:border-primary pb-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                navFontWeightClass,
                navTextTransformClass
              )}
              aria-current={typeof window !== 'undefined' && window.location.pathname === item.path ? 'page' : undefined}
            >
              {item.label}
            </a>
          ))}
          {categoryPosition === 'in-nav' && (
            <CategoryMenu config={categoryMenuConfig} navLinkClassName={cn(navFontWeightClass, navTextTransformClass)} />
          )}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-4 sm:gap-6 justify-end md:flex-1 ml-auto">
          {/* Locale switcher (desktop) — issue #140 */}
          <div className="hidden md:block">
            <LocaleSwitcher />
          </div>

          {/* Theme toggle (only renders when the active theme supports dark mode) */}
          <ThemeToggle />

          {/* Search Icon */}
          <button
            className="text-text-secondary hover:text-primary transition-colors p-2 -m-2 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label="Search"
            data-testid="search-button"
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
          </button>

          {/* Cart Button */}
          <button
            onClick={toggleCartDrawer}
            className="flex items-center gap-2 text-text-secondary hover:text-primary transition-colors relative p-2 -m-2 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label="Shopping cart"
            data-testid="cart-button"
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M6 2L3 6v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6l-3-4H6zM3 6h18M16 10a4 4 0 1 1-8 0"/>
            </svg>
            {totals.itemCount > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-primary text-[var(--color-text-on-primary)] rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium"
                data-testid="cart-count"
                aria-label={`${totals.itemCount} items in cart`}
              >
                {totals.itemCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Desktop Below-Nav Category Bar */}
      {categoryPosition === 'below-nav' && (
        <div className="hidden md:block border-t border-border-light">
          <div
            className="container mx-auto px-lg py-2 flex items-center justify-center"
            style={{ maxWidth: 'var(--container-max-width)' }}
          >
            <CategoryMenu config={categoryMenuConfig} navLinkClassName={cn(navFontWeightClass, navTextTransformClass)} />
          </div>
        </div>
      )}

      {/* Mobile Navigation Drawer */}
      <MobileNav
        isOpen={mobileMenuOpen}
        onClose={closeMobileMenu}
        navigationItems={navigationItems}
        categoryMenuConfig={categoryMenuConfig}
        navFontWeightClass={navFontWeightClass}
        navTextTransformClass={navTextTransformClass}
      />
    </header>
  );
};

export default Header;
